import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { api } from '../lib/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Calendar, Clock, Star, Music, Sparkles, BadgeCheck, Lock, ArrowRight, LogIn } from 'lucide-react';

const GENRE_CONFIG = {
  dancehall: { label: 'Dancehall', color: 'bg-lime/20 text-lime border-lime/30' },
  reggae: { label: 'Reggae', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  soca: { label: 'Soca', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
};

function formatReset(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', { weekday: 'long', month: 'short', day: 'numeric', hour: 'numeric', timeZone: 'America/New_York', timeZoneName: 'short' });
  } catch {
    return '';
  }
}

export default function WeeklySpecials() {
  const [artists, setArtists] = useState([]);
  const [nextReset, setNextReset] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated, user } = useAuth();

  const isArtist = user?.role === 'artist';
  const isAgent = user?.role === 'agent';
  const canSeePrices = isAuthenticated && !isArtist && !isAgent;

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        const res = await api.getWeeklySpecials();
        setArtists(res.data.artists || []);
        setNextReset(res.data.next_reset);
      } catch {
        toast.error('Failed to load weekly specials');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isAuthenticated]);

  const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.08 } } };
  const itemVariants = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />

      <section className="relative pt-28 pb-12 overflow-hidden">
        <div className="hero-gradient absolute inset-0 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-lime" />
              <span className="text-sm text-lime font-medium uppercase tracking-wider">Weekly Specials</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
              Recording <span className="text-lime">This Week</span>
            </h1>
            <p className="text-lg text-[#A1A1AA] max-w-3xl">
              These artists are recording this week with our verified dubplate agents. Book yours now.
            </p>
            {nextReset && (
              <div className="mt-6 inline-flex items-center gap-2 text-sm text-[#A1A1AA] bg-surface border border-[#262626] rounded-full px-4 py-2">
                <Calendar className="w-4 h-4 text-lime" />
                <span>Lineup resets {formatReset(nextReset)}</span>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {!isAuthenticated ? (
            <div className="text-center py-16 bg-surface rounded-2xl border border-[#262626]" data-testid="weekly-specials-login-gate">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-lime/10 flex items-center justify-center">
                <Lock className="w-7 h-7 text-lime" />
              </div>
              <h3 className="text-2xl font-heading font-bold mb-2">Log in to see more</h3>
              <p className="text-[#A1A1AA] mb-6 max-w-md mx-auto">
                Weekly Specials are reserved for Tune4Tune members. Sign in to see which artists are recording this week.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Link to="/login?returnTo=/weekly-specials" className="btn-primary inline-flex items-center gap-2" data-testid="weekly-specials-login-btn">
                  <LogIn className="w-4 h-4" />
                  Log In
                </Link>
                <Link to="/register" className="btn-secondary" data-testid="weekly-specials-signup-btn">
                  Create Account
                </Link>
              </div>
            </div>
          ) : loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="artist-card animate-pulse">
                  <div className="aspect-[4/3] bg-surface-highlight" />
                  <div className="p-6">
                    <div className="h-6 bg-surface-highlight rounded mb-3 w-3/4" />
                    <div className="h-4 bg-surface-highlight rounded mb-4 w-full" />
                    <div className="h-8 bg-surface-highlight rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : artists.length === 0 ? (
            <div className="text-center py-24" data-testid="weekly-specials-empty">
              <Music className="w-16 h-16 mx-auto mb-4 text-[#333]" />
              <h3 className="text-2xl font-heading font-bold mb-2">No specials available</h3>
              <p className="text-[#A1A1AA]">Please check back later.</p>
            </div>
          ) : (
            <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {artists.map((artist, idx) => (
                <motion.div key={artist.id} variants={itemVariants}>
                  <Link
                    to={`/artist/${artist.id}?agent=${artist.agent_id}`}
                    className="artist-card block group"
                    data-testid={`weekly-special-${idx}`}
                  >
                    <div className="aspect-[4/3] relative overflow-hidden">
                      <img src={artist.image_url} alt={artist.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                      <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 rounded-full bg-lime/90 text-black text-xs font-bold">
                        <Sparkles className="w-3 h-3" /> This Week
                      </div>
                      <div className="absolute bottom-4 left-4 right-4">
                        {canSeePrices ? (
                          <div className="flex items-center gap-2">
                            <span className="price-tag">${artist.price.toFixed(2)}</span>
                            {artist.original_price != null && artist.original_price !== artist.price && (
                              <span className="text-xs text-[#A1A1AA] line-through" data-testid={`original-price-${idx}`}>
                                ${artist.original_price.toFixed(2)}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="price-tag flex items-center gap-1">
                            <Lock className="w-3 h-3" />
                            {isArtist || isAgent ? 'Hidden' : 'Login to see price'}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="font-heading text-xl font-bold mb-2 group-hover:text-lime transition-colors flex items-center gap-2">
                        {artist.name}
                        {artist.is_verified && <BadgeCheck className="w-5 h-5 text-blue-400" />}
                      </h3>
                      {artist.genres?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {artist.genres.slice(0, 2).map((g) => (
                            <span key={g} className={`text-xs px-2 py-0.5 rounded-full border ${GENRE_CONFIG[g]?.color || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}>
                              {GENRE_CONFIG[g]?.label || g}
                            </span>
                          ))}
                        </div>
                      )}
                      <p className="text-sm text-[#A1A1AA] mb-4 line-clamp-2">{artist.bio}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-[#666]">
                          <Clock className="w-4 h-4" />
                          <span>{artist.delivery_days} days</span>
                        </div>
                        {canSeePrices && artist.average_rating && (
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-lime fill-lime" />
                            <span className="text-sm text-lime">{artist.average_rating}</span>
                          </div>
                        )}
                        <ArrowRight className="w-5 h-5 text-lime opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
